import React, { useState, useEffect } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { formatDistanceToNow } from "date-fns";
import FilterBar from "../studentDistribution/FilterBar";
import { cleaningApiBaseUrlFromEnv } from "../../runtime/loadSharedDashboardConfig";

interface CameraStat {
  sensor_id: string;
  camera_name: string;
  location_name: string;
  location_id?: string;
  coverage: string;
  status: string;
  last_cleaned: string;
  image_link: string | null;
}

interface CleaningCameraHeatmapProps {
  date: string;
}

const CleaningCameraHeatmap: React.FC<CleaningCameraHeatmapProps> = ({ date }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const [allCameras, setAllCameras] = useState<CameraStat[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const fetchCameraStats = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (date) params.append("date", date);
        const response = await fetch(`${cleaningApiBaseUrlFromEnv()}/cleaning/camera-stats?${params.toString()}`, {
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
        });
        if (response.ok) {
          const data = await response.json();
          const items = Array.isArray(data) ? data : (data as any)?.data ?? [];
          setAllCameras(items);
        }
      } catch (error) {
        if ((error as DOMException)?.name !== "AbortError") {
          console.error("Failed to fetch camera stats:", error);
          setAllCameras([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchCameraStats();
    return () => controller.abort();
  }, [date]);

  const dynamicLocations = React.useMemo(() => {
    const locMap = new Map<string, string>();
    allCameras.forEach((cam) => {
      if (cam.location_id && cam.location_name) {
        locMap.set(cam.location_id, cam.location_name);
      }
    });
    return Array.from(locMap.entries()).map(([id, label]) => ({ label, value: id }));
  }, [allCameras]);

  const displayedCameras = React.useMemo(() => {
    if (!selectedLocationId) return allCameras;
    return allCameras.filter((c) => c.location_id === selectedLocationId);
  }, [allCameras, selectedLocationId]);

  const getFormattedTime = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const dateObj = new Date(dateString);
      const relative = formatDistanceToNow(dateObj, { addSuffix: true });
      return relative.startsWith("in ") ? relative.replace("in ", "") + " ago" : relative;
    } catch {
      return "N/A";
    }
  };

  return (
    <div className="mt-8 flex flex-col gap-6 text-right">
      {/* Filter Section */}
      <div className="flex justify-end">
        <FilterBar
          filters={[
            {
              label: "Location",
              value: selectedLocationId,
              onChange: setSelectedLocationId,
              options: dynamicLocations,
              placeholder: "All Locations",
            },
          ]}
          className="!mb-0 shadow-sm"
        />
      </div>

      {/* Content Display */}
      <div className={`rounded-2xl p-6 ${isDarkMode ? "bg-[#1C202F]/50 border border-gray-800" : "bg-white border border-gray-100 shadow-sm"}`}>
        {displayedCameras.length === 0 && !loading ? (
          <div className={`h-64 flex items-center justify-center rounded-xl ${isDarkMode ? "bg-gray-800/20" : "bg-gray-50"}`}>
            <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>No camera data found for this location.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-12">
            {loading && displayedCameras.length === 0 ? (
              <div className="h-64 flex items-center justify-center">
                <p className={isDarkMode ? "text-gray-500" : "text-gray-400"}>Loading...</p>
              </div>
            ) : (
              displayedCameras.map((camera, index) => (
                <div key={camera.sensor_id || index} className="flex flex-col gap-6">
                  <div className="border-b border-gray-800 pb-2 mb-2">
                    <h3 className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                      {camera.camera_name}
                      {camera.location_name && (
                        <span className={`ml-2 text-sm font-normal ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                          ({camera.location_name})
                        </span>
                      )}
                    </h3>
                  </div>

                  {/* Heatmap Image */}
                  <div
                    className={`w-full h-[350px] md:h-[400px] flex items-center justify-center overflow-hidden rounded-xl relative ${isDarkMode ? "bg-[#0b0d14] border border-gray-800" : "bg-gray-50 bg-opacity-50"}`}
                  >
                    {loading ? (
                      <p className={isDarkMode ? "text-gray-500" : "text-gray-400"}>Loading...</p>
                    ) : camera?.image_link ? (
                      <>
                        <img
                          src={camera.image_link}
                          alt={`Heatmap for ${camera?.camera_name}`}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = "none";
                            (target.nextElementSibling as HTMLElement)?.classList.remove("hidden");
                            (target.nextElementSibling as HTMLElement)?.classList.add("flex");
                          }}
                        />
                        <div
                          className={`hidden absolute inset-0 items-center justify-center flex-col gap-3 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
                        >
                          <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="font-medium">Image Not Available</span>
                        </div>
                      </>
                    ) : (
                      <div
                        className={`flex absolute inset-0 items-center justify-center flex-col gap-3 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
                      >
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="font-medium">Image Not Available</span>
                      </div>
                    )}
                  </div>

                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-left">
                    <div
                      className={`px-3 py-2.5 rounded-lg flex flex-col justify-center border ${isDarkMode ? "bg-[#1c4a45]/20 border-[#2A6B64]/30" : "bg-emerald-50 border-emerald-100"}`}
                    >
                      <h4 className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${isDarkMode ? "text-[#2A6B64]" : "text-emerald-600"}`}>Coverage</h4>
                      <p className={`text-base font-bold leading-tight ${isDarkMode ? "text-white" : "text-gray-900"}`}>{camera?.coverage || "-"}</p>
                    </div>

                    <div
                      className={`px-3 py-2.5 rounded-lg flex flex-col justify-center border ${isDarkMode ? "bg-[#3d2e5a]/20 border-[#594485]/30" : "bg-purple-50 border-purple-100"}`}
                    >
                      <h4 className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${isDarkMode ? "text-[#594485]" : "text-purple-600"}`}>Last Cleaned</h4>
                      <p className={`text-sm font-bold leading-tight ${isDarkMode ? "text-white" : "text-gray-900"}`}>{getFormattedTime(camera?.last_cleaned)}</p>
                    </div>

                    <div
                      className={`px-3 py-2.5 rounded-lg flex flex-col justify-center border ${isDarkMode ? "bg-[#423215]/20 border-[#5E4A1D]/30" : "bg-amber-50 border-amber-100"}`}
                    >
                      <h4 className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${isDarkMode ? "text-[#5E4A1D]" : "text-amber-600"}`}>Status</h4>
                      <p className={`text-base font-bold leading-tight ${isDarkMode ? "text-white" : "text-gray-900"}`}>{camera?.status || "-"}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CleaningCameraHeatmap;
